import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Mail, Trash2, Plus, ExternalLink, Download, RefreshCw, Users, CalendarRange, ArrowLeft, FileText, Save, RotateCcw } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "rrdc_admin_token";
const LOGO_IMG =
  "https://customer-assets.emergentagent.com/job_audition-stage-1/artifacts/2bcwv5i5_rr%20logo.png";

const fmtDate = (iso) => { try { return format(parseISO(iso), "MMM d, yyyy • h:mm a"); } catch { return iso; } };

function Login({ onAuth }) {
  const [passcode, setPasscode] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/admin/login`, { passcode });
      localStorage.setItem(TOKEN_KEY, r.data.token);
      onAuth(r.data.token);
    } catch {
      toast.error("Incorrect passcode");
    } finally { setLoading(false); }
  };
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#3556A5] px-6">
      <div className="w-full max-w-sm rounded-3xl bg-white p-8 shadow-2xl">
        <div className="mb-6 flex flex-col items-center text-center">
          <img src={LOGO_IMG} alt="Rolling River Day Camp" className="mb-3 h-20 w-20 rounded-full object-contain" />
          <h1 className="font-heading text-2xl font-semibold text-[#3556A5]">Director Login</h1>
          <p className="mt-1 text-sm text-slate-500">Rolling River Drama — Admin</p>
        </div>
        <Label className="mb-2 block text-xs font-bold uppercase tracking-wide text-slate-600">Passcode</Label>
        <Input
          type="password" value={passcode} data-testid="admin-passcode-input"
          onChange={(e) => setPasscode(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && submit()}
          className="h-12 rounded-xl bg-slate-50" placeholder="Enter passcode"
        />
        <Button onClick={submit} disabled={loading || !passcode} data-testid="admin-login-btn"
          className="mt-5 h-12 w-full rounded-full bg-[#3556A5] font-semibold text-white hover:bg-[#2B4584]">
          {loading ? "Checking…" : "Enter"}
        </Button>
        <Link to="/" data-testid="back-to-form-link"
          className="mt-4 flex items-center justify-center gap-2 text-sm font-semibold text-slate-500 hover:text-[#3556A5]">
          <ArrowLeft className="h-4 w-4" /> Back to audition form
        </Link>
      </div>
    </div>
  );
}
function SessionsEditor({ token }) {
  const [labels, setLabels] = useState(null);
  const [saving, setSaving] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/sessions`, { headers });
      setLabels((r.data.sessions || []).map((s) => s.label));
    } catch { /* ignore */ }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const setAt = (i, v) => setLabels((l) => l.map((x, idx) => (idx === i ? v : x)));
  const addRow = () => setLabels((l) => [...l, ""]);

  const persist = async (nextLabels) => {
    const clean = nextLabels.map((s) => s.trim()).filter(Boolean);
    if (clean.length === 0) { toast.error("Keep at least one session"); return false; }
    const r = await axios.put(`${API}/admin/sessions`, { sessions: clean }, { headers });
    setLabels(r.data.sessions.map((s) => s.label));
    return true;
  };

  // Removing a session saves immediately so it persists.
  const removeRow = async (i) => {
    const next = (labels || []).filter((_, idx) => idx !== i);
    if (next.map((s) => s.trim()).filter(Boolean).length === 0) {
      toast.error("Keep at least one session");
      return;
    }
    setLabels(next);
    try {
      await persist(next);
      toast.success("Session removed");
    } catch {
      toast.error("Could not remove session");
      load();
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const ok = await persist(labels || []);
      if (ok) toast.success("Sessions saved");
    } catch {
      toast.error("Could not save sessions");
    } finally { setSaving(false); }
  };

  const reset = async () => {
    try {
      const r = await axios.post(`${API}/admin/sessions/reset`, {}, { headers });
      setLabels(r.data.sessions.map((s) => s.label));
      toast.success("Reset to default sessions");
    } catch { toast.error("Could not reset"); }
  };

  return (
    <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm" data-testid="sessions-editor">
      <div className="mb-2 flex items-center gap-2">
        <CalendarRange className="h-5 w-5 text-[#3556A5]" />
        <h2 className="font-heading text-lg font-semibold text-[#3556A5]">Session Options</h2>
      </div>
      <p className="mb-4 text-sm text-slate-500">These are the sessions families choose from on the audition form. Removing a session saves instantly. To add or rename a session, edit above and click <strong>Save sessions</strong>.</p>
      {!labels ? (
        <p className="py-4 text-center text-slate-400">Loading…</p>
      ) : (
        <>
          <div className="space-y-2">
            {labels.map((label, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input value={label} onChange={(e) => setAt(i, e.target.value)} data-testid={`session-input-${i}`}
                  placeholder="Session name" className="h-11 rounded-xl bg-slate-50" />
                <Button variant="ghost" size="icon" onClick={() => removeRow(i)} data-testid={`remove-session-${i}`}
                  className="h-11 w-11 shrink-0 rounded-xl text-slate-400 hover:bg-red-50 hover:text-red-600">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
          <div className="mt-4 flex flex-wrap gap-3">
            <Button onClick={addRow} variant="outline" data-testid="add-session-btn" className="h-11 rounded-full border-slate-300 px-5 font-semibold">
              <Plus className="mr-1 h-4 w-4" /> Add session
            </Button>
            <Button onClick={save} disabled={saving} data-testid="save-sessions-btn" className="h-11 rounded-full bg-[#3556A5] px-6 font-semibold text-white hover:bg-[#2B4584]">
              <Save className="mr-2 h-4 w-4" /> {saving ? "Saving…" : "Save sessions"}
            </Button>
            <Button onClick={reset} variant="outline" data-testid="reset-sessions-btn" className="h-11 rounded-full border-slate-300 px-5 font-semibold">
              <RotateCcw className="mr-2 h-4 w-4" /> Reset
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

const CONTENT_FIELDS = [
  { key: "nav_brand", label: "Navbar brand name" },
  { key: "hero_badge", label: "Hero badge label (year is added automatically)" },
  { key: "hero_title", label: "Hero heading", ml: true },
  { key: "hero_subtitle", label: "Hero paragraph", ml: true },
  { key: "camper_section_title", label: "Camper section title" },
  { key: "camper_section_subtitle", label: "Camper section subtitle" },
  { key: "parent_section_title", label: "Parent section title" },
  { key: "parent_section_subtitle", label: "Parent section subtitle" },
  { key: "session_section_title", label: "Session section title" },
  { key: "session_section_subtitle", label: "Session question" },
  { key: "video_section_title", label: "Video section title" },
  { key: "video_helper", label: "Video upload helper text", ml: true },
  { key: "commitment_title", label: "Commitment box title" },
  { key: "commitment_text", label: "Commitment statement", ml: true },
  { key: "commitment_checkbox_label", label: "Commitment checkbox label", ml: true },
  { key: "submit_button", label: "Submit button label" },
  { key: "success_heading", label: "Success heading (before camper name)" },
  { key: "success_message", label: "Success message" },
  { key: "success_subtext", label: "Success subtext", ml: true },
  { key: "success_closing", label: "Success closing line" },
];

function ContentEditor({ token }) {
  const [values, setValues] = useState(null);
  const [saving, setSaving] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/content`, { headers });
      setValues(r.data.content);
    } catch { /* ignore */ }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const setField = (k, v) => setValues((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/admin/content`, values, { headers });
      setValues(r.data.content);
      toast.success("Home page content saved");
    } catch {
      toast.error("Could not save content");
    } finally { setSaving(false); }
  };

  const reset = async () => {
    try {
      const r = await axios.post(`${API}/admin/content/reset`, {}, { headers });
      setValues(r.data.content);
      toast.success("Reset to default text");
    } catch {
      toast.error("Could not reset");
    }
  };

  return (
    <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm" data-testid="content-editor">
      <div className="mb-2 flex items-center gap-2">
        <FileText className="h-5 w-5 text-[#3556A5]" />
        <h2 className="font-heading text-lg font-semibold text-[#3556A5]">Home Page Content</h2>
      </div>
      <p className="mb-5 text-sm text-slate-500">
        Edit any of the text shown on the public audition form. Changes go live as soon as you save.
      </p>

      {!values ? (
        <p className="py-6 text-center text-slate-400">Loading…</p>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            {CONTENT_FIELDS.map((f) => (
              <div key={f.key} className={f.ml ? "md:col-span-2" : ""}>
                <Label className="mb-2 block text-xs font-bold uppercase tracking-wide text-slate-600">{f.label}</Label>
                {f.ml ? (
                  <Textarea
                    value={values[f.key] || ""}
                    onChange={(e) => setField(f.key, e.target.value)}
                    data-testid={`content-${f.key}`}
                    rows={3}
                    className="rounded-xl bg-slate-50"
                  />
                ) : (
                  <Input
                    value={values[f.key] || ""}
                    onChange={(e) => setField(f.key, e.target.value)}
                    data-testid={`content-${f.key}`}
                    className="h-11 rounded-xl bg-slate-50"
                  />
                )}
              </div>
            ))}
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <Button onClick={save} disabled={saving} data-testid="save-content-btn"
              className="h-11 rounded-full bg-[#3556A5] px-6 font-semibold text-white hover:bg-[#2B4584]">
              <Save className="mr-2 h-4 w-4" /> {saving ? "Saving…" : "Save changes"}
            </Button>
            <Button onClick={reset} variant="outline" data-testid="reset-content-btn"
              className="h-11 rounded-full border-slate-300 px-6 font-semibold">
              <RotateCcw className="mr-2 h-4 w-4" /> Reset to defaults
            </Button>
          </div>
        </>
      )}
    </div>
  );
}



function SeasonManager({ token, onChanged }) {
  const [info, setInfo] = useState({ current_season: null, audition_count: 0 });
  const [year, setYear] = useState("");
  const [saving, setSaving] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/season`, { headers });
      setInfo(r.data);
      setYear(String(r.data.current_season));
    } catch { /* ignore */ }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const apply = async () => {
    const y = parseInt(year, 10);
    if (!y || y < 2000 || y > 2100) { toast.error("Enter a valid year"); return; }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/admin/season`, { year: y }, { headers });
      toast.success(`Season set to ${r.data.current_season}` + (r.data.deleted_count ? ` • ${r.data.deleted_count} old record(s) cleared` : ""));
      await load();
      onChanged && onChanged();
    } catch {
      toast.error("Could not update season");
    } finally { setSaving(false); }
  };

  const yearChanged = parseInt(year, 10) !== info.current_season;

  return (
    <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm" data-testid="season-manager">
      <div className="mb-4 flex items-center gap-2">
        <CalendarRange className="h-5 w-5 text-[#3556A5]" />
        <h2 className="font-heading text-lg font-semibold text-[#3556A5]">Audition Season</h2>
      </div>
      <p className="mb-4 text-sm text-slate-500">
        Switch the active season each year. Auditions are tagged to the current season; the dashboard shows
        only this season&#39;s submissions. Starting a new season clears the previous season&#39;s records (not retained).
      </p>
      <div className="flex flex-wrap items-end gap-4">
        <div>
          <Label className="mb-2 block text-xs font-bold uppercase tracking-wide text-slate-600">Current season</Label>
          <span className="inline-flex items-center rounded-full bg-[#3556A5] px-5 py-2 font-heading text-lg font-semibold text-white" data-testid="current-season-badge">
            {info.current_season || "—"}
          </span>
        </div>
        <div className="w-40">
          <Label className="mb-2 block text-xs font-bold uppercase tracking-wide text-slate-600">Set season year</Label>
          <Input type="number" min="2000" max="2100" value={year} onChange={(e) => setYear(e.target.value)} data-testid="season-year-input" className="h-11 rounded-xl bg-slate-50" />
        </div>

        {yearChanged ? (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button data-testid="switch-season-btn" className="h-11 rounded-full bg-[#C9A227] px-6 font-semibold text-white hover:bg-[#A88720]">
                Start {year} Season
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="rounded-3xl">
              <AlertDialogHeader>
                <AlertDialogTitle>Start the {year} season?</AlertDialogTitle>
                <AlertDialogDescription>
                  This sets the active season to <strong>{year}</strong> and permanently clears all audition
                  records from other seasons (including the current {info.current_season} season&#39;s {info.audition_count} submission{info.audition_count === 1 ? "" : "s"}). This cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={apply} disabled={saving} className="bg-[#3556A5] hover:bg-[#2B4584]" data-testid="confirm-switch-season-btn">
                  {saving ? "Switching…" : "Yes, start new season"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        ) : (
          <Button disabled className="h-11 rounded-full px-6">Active</Button>
        )}
      </div>
    </div>
  );
}


function RecipientsManager({ token }) {
  const [data, setData] = useState({ env_recipients: [], extra_recipients: [] });
  const [email, setEmail] = useState("");
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/admin/recipients`, { headers }); setData(r.data); }
    catch { /* ignore */ }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!email) return;
    try { const r = await axios.post(`${API}/admin/recipients`, { email }, { headers });
      setData((d) => ({ ...d, active: r.data.active })); setEmail(""); toast.success("Recipient added"); }
    catch (e) { toast.error(e?.response?.data?.detail ? "Invalid email" : "Could not add recipient"); }
  };
  const remove = async (em) => {
    try { const r = await axios.delete(`${API}/admin/recipients`, { headers, params: { email: em } });
      setData((d) => ({ ...d, active: r.data.active })); toast.success("Removed"); }
    catch { toast.error("Could not remove"); }
  };

  const active = data.active || [];

  return (
    <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm" data-testid="recipients-manager">
      <div className="mb-4 flex items-center gap-2">
        <Mail className="h-5 w-5 text-[#3556A5]" />
        <h2 className="font-heading text-lg font-semibold text-[#3556A5]">Notification Recipients</h2>
      </div>
      <p className="mb-3 text-sm text-slate-500">Directors who receive a copy of every new audition email. Remove any address you don&#39;t want to receive emails.</p>
      <div className="mb-4 flex flex-wrap gap-2">
        {active.length === 0 && (
          <span className="text-sm text-slate-400" data-testid="no-recipients">No one will receive audition emails. Add a recipient below.</span>
        )}
        {active.map((e) => (
          <span key={e} className="inline-flex items-center gap-2 rounded-full bg-[#3556A5]/10 px-3 py-1 text-sm font-semibold text-[#3556A5]">
            {e}
            <button onClick={() => remove(e)} data-testid={`remove-recipient-${e}`} className="text-[#3556A5] hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <Input value={email} onChange={(e) => setEmail(e.target.value)} data-testid="recipient-email-input"
          onKeyDown={(e) => e.key === "Enter" && add()} placeholder="director@email.com" className="h-11 rounded-xl bg-slate-50" />
        <Button onClick={add} data-testid="add-recipient-btn" className="h-11 rounded-full bg-[#C9A227] px-5 font-semibold text-white hover:bg-[#A88720]">
          <Plus className="mr-1 h-4 w-4" /> Add
        </Button>
      </div>
    </div>
  );
}

function DetailDialog({ auditionId, token, open, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!open || !auditionId) return;
    setData(null);
    axios.get(`${API}/admin/auditions/${auditionId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setData(r.data)).catch(() => toast.error("Could not load details"));
  }, [auditionId, open, token]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto rounded-3xl" data-testid="audition-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-[#3556A5]">
            {data ? `${data.camper.first_name} ${data.camper.last_name}` : "Loading…"}
          </DialogTitle>
        </DialogHeader>
        {data && (
          <div className="space-y-5">
            {data.video && data.video.url ? (
              <video src={data.video.url} controls data-testid="detail-video" className="w-full rounded-2xl bg-black" />
            ) : (
              <div className="flex items-center justify-center gap-2 rounded-2xl bg-[#3556A5]/5 p-6 text-sm font-semibold text-[#3556A5]" data-testid="detail-no-video">
                Stage Crew — no audition video submitted
              </div>
            )}
            <div className="grid grid-cols-2 gap-3 text-sm">
              <Info label="Auditioning For" value={data.role_label || "—"} />
              <Info label="Grade" value={data.camper.grade} />
              <Info label="Session" value={data.session_label} />
              <Info label="Date of Birth" value={data.camper.dob} />
              <Info label="Camper Phone" value={data.camper.phone || "—"} />
              <Info label="Parent" value={`${data.parent.first_name} ${data.parent.last_name}`} />
              <Info label="Parent Phone" value={data.parent.phone} />
              <Info label="Parent Email" value={data.parent.email} />
              <Info label="Submitted" value={fmtDate(data.created_at)} />
              <div className="col-span-2">
                <Info label="Address" value={`${data.camper.address.street}, ${data.camper.address.city}, ${data.camper.address.state} ${data.camper.address.zip}`} />
              </div>
            </div>

            <div className="rounded-2xl border border-[#C9A227]/40 bg-[#C9A227]/[0.06] p-4">
              <p className="mb-1 text-xs font-bold uppercase tracking-wide text-[#A88720]">Commitment Signed</p>
              <p className="text-sm text-slate-600">By {data.commitment.signed_by} on {fmtDate(data.commitment.signed_at)}</p>
              <div className="mt-3 rounded-xl border border-slate-200 bg-white p-3" data-testid="detail-signature">
                {data.commitment.signature_type === "canvas"
                  ? <img src={data.commitment.signature_data} alt="signature" className="h-24 object-contain" />
                  : <span className="font-signature text-4xl text-slate-800">{data.commitment.signature_data}</span>}
              </div>
            </div>

            {data.video && data.video.url && (
              <div className="flex flex-wrap items-center gap-4">
                <a href={`${data.video.url}?download=1`}
                  className="inline-flex items-center gap-2 rounded-full bg-[#3556A5] px-4 py-2 text-sm font-semibold text-white hover:bg-[#2B4584]"
                  data-testid="detail-download-video">
                  <Download className="h-4 w-4" /> Download video
                </a>
                <a href={data.video.url} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-[#3556A5] hover:underline">
                  <ExternalLink className="h-4 w-4" /> Open in new tab
                </a>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const Info = ({ label, value }) => (
  <div>
    <p className="text-xs font-bold uppercase tracking-wide text-slate-400">{label}</p>
    <p className="font-semibold text-slate-700">{value}</p>
  </div>
);

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || "");
  const [auditions, setAuditions] = useState([]);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sessionOpts, setSessionOpts] = useState([{ value: "all", label: "All Sessions" }]);

  useEffect(() => {
    axios.get(`${API}/config`).then((r) => {
      const opts = [{ value: "all", label: "All Sessions" }, ...(r.data.sessions || [])];
      setSessionOpts(opts);
    }).catch(() => {});
  }, [token]);

  const load = useCallback(async (t, sess) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/auditions`, {
        headers: { Authorization: `Bearer ${t}` }, params: { session: sess },
      });
      setAuditions(r.data.auditions);
    } catch (e) {
      if (e?.response?.status === 401) { localStorage.removeItem(TOKEN_KEY); setToken(""); }
      else toast.error("Could not load auditions");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (token) load(token, filter); }, [token, filter, load]);

  const logout = () => { localStorage.removeItem(TOKEN_KEY); setToken(""); };

  const exportCsv = async () => {
    try {
      const r = await axios.get(`${API}/admin/auditions/export`, {
        headers: { Authorization: `Bearer ${token}` }, params: { session: filter }, responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `rolling-river-auditions.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not export submissions");
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/admin/auditions/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setAuditions((list) => list.filter((a) => a.id !== id));
      if (selected === id) setSelected(null);
      toast.success("Submission deleted");
    } catch {
      toast.error("Could not delete submission");
    }
  };

  if (!token) return <Login onAuth={setToken} />;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="bg-[#3556A5] px-6 py-5">
        <div className="mx-auto flex max-w-6xl items-center justify-between">
          <div className="flex items-center gap-2.5 text-white">
            <img src={LOGO_IMG} alt="Rolling River Day Camp" className="h-9 w-9 rounded-full bg-white object-contain p-0.5" />
            <span className="font-heading text-lg font-semibold">Rolling River Drama — Director Dashboard</span>
          </div>
          <Button onClick={logout} variant="ghost" data-testid="admin-logout-btn" className="text-white hover:bg-white/10">Log out</Button>
        </div>
      </header>

      <main className="mx-auto max-w-6xl space-y-6 px-4 py-8 sm:px-6">
        <SeasonManager token={token} onChanged={() => load(token, filter)} />
        <SessionsEditor token={token} />
        <ContentEditor token={token} />
        <RecipientsManager token={token} />

        <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm">
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-[#3556A5]" />
              <h2 className="font-heading text-lg font-semibold text-[#3556A5]">Auditions ({auditions.length})</h2>
            </div>
            <div className="flex items-center gap-2">
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="h-10 w-44 rounded-xl bg-slate-50" data-testid="session-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {sessionOpts.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button onClick={exportCsv} disabled={auditions.length === 0} variant="outline" data-testid="export-csv-btn" className="h-10 rounded-xl gap-2">
                <Download className="h-4 w-4" /> Download CSV
              </Button>
              <Button onClick={() => load(token, filter)} variant="outline" size="icon" data-testid="refresh-btn" className="h-10 w-10 rounded-xl">
                <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              </Button>
            </div>
          </div>

          {auditions.length === 0 ? (
            <p className="py-12 text-center text-slate-400" data-testid="no-auditions">No auditions yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="auditions-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Camper Name</TableHead>
                    <TableHead>Grade</TableHead>
                    <TableHead>Audition Type</TableHead>
                    <TableHead>Session</TableHead>
                    <TableHead>Parent Email</TableHead>
                    <TableHead>Submitted</TableHead>
                    <TableHead>Video</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {auditions.map((a) => (
                    <TableRow key={a.id} onClick={() => setSelected(a.id)} data-testid={`audition-row-${a.id}`} className="cursor-pointer hover:bg-[#3556A5]/5">
                      <TableCell className="font-semibold text-slate-800">{a.camper_name}</TableCell>
                      <TableCell>{a.grade}</TableCell>
                      <TableCell><Badge className="rounded-full bg-[#C9A227]/15 text-[#A88720] hover:bg-[#C9A227]/15">{a.role_label || "—"}</Badge></TableCell>
                      <TableCell><Badge className="rounded-full bg-[#3556A5]/10 text-[#3556A5] hover:bg-[#3556A5]/10">{a.session_label}</Badge></TableCell>
                      <TableCell className="text-slate-600">{a.parent_email}</TableCell>
                      <TableCell className="text-slate-600">{fmtDate(a.created_at)}</TableCell>
                      <TableCell>
                        {a.video_url ? (
                          <div className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
                            <a href={a.video_url} target="_blank" rel="noreferrer"
                              className="inline-flex items-center gap-1 font-semibold text-[#C9A227] hover:underline">
                              <ExternalLink className="h-4 w-4" /> Watch
                            </a>
                            <a href={`${a.video_url}?download=1`}
                              className="inline-flex items-center gap-1 font-semibold text-[#3556A5] hover:underline"
                              data-testid={`download-audition-${a.id}`}>
                              <Download className="h-4 w-4" /> Download
                            </a>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400">No video</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`delete-audition-${a.id}`} className="h-9 w-9 rounded-xl text-slate-400 hover:bg-red-50 hover:text-red-600">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="rounded-3xl">
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete this submission?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This permanently removes <strong>{a.camper_name}</strong>&#39;s audition and its video. This cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => remove(a.id)} data-testid={`confirm-delete-${a.id}`} className="bg-red-600 hover:bg-red-700">
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </main>

      <DetailDialog auditionId={selected} token={token} open={!!selected} onClose={() => setSelected(null)} />
    </div>
  );
}
